import React from 'react';
import { CollapsibleCards } from '../collapsible-cards';
import { Stack, Flex, Text, Image, Box } from '@tonic-ui/react';
import { Trans, useTranslation } from 'react-i18next';

import CloudComplianceIcon from '../../../assets/landing/SVG/icon_1.svg';
import CloudNativeIcon from '../../../assets/landing/SVG/icon_2.svg';
import CloudDataCenterIcon from '../../../assets/landing/SVG/icon_3.svg';
import cloudGraphic from '../../../assets/images/cloud-diagram.png';
import govRound from '../../../assets/images/govRound.png';
import { PLExternalButtonLink } from '../tonic-ui-overrides';
import outlineBackground from '../../../assets/landing/SVG/outlineBackground.svg';
import cloudOneGovernmentDiagram from '../../../assets/images/trendMicroCloudOneForGovernmentDiagram.png';
import { isGovCloudRegion } from '../../../utils/helpers';
import cloudIcons from '../../../assets/landing/img/cloudIcons.png';
import fedRampLogo from '../../../assets/images//fed_ramp.png';

interface DetailsBodyProps {
  content: string;
  learnMoreLink: string;
}

const DetailsBody = ({ content, learnMoreLink }: DetailsBodyProps) => {
  return (
    <>
      <Text size="xl" pb="2x" pt="4x" color="black">
        {content}
      </Text>
      {learnMoreLink && (
        <Flex pt="6x" pb="5x" align="center">
          <PLExternalButtonLink href={learnMoreLink}>
            <Trans i18nKey="publicLandingPage.learnMore" />
          </PLExternalButtonLink>
        </Flex>
      )}
    </>
  );
};

export const SecurityConcernsSection = () => {
  const { t } = useTranslation();
  const tileContentKey = isGovCloudRegion ? 'govRedTileContent' : 'redTileContent';
  return (
    <Stack overflow="hidden" alignItems="center" paddingX="10%" margin="auto">
      {isGovCloudRegion && (
        <Flex
          direction={{ sm: 'column', md: 'column', lg: 'row' }}
          justifyContent="center"
          width="100%"
          marginBottom="80px"
          alignItems="center"
          gap={{ sm: '80px', lg: '140px' }}
        >
          <Box position="relative">
            <Image
              src={govRound}
              maxWidth="667px"
              maxHeight="360px"
              position="relative"
              zIndex="1"
            />
            <Text
              as="sub"
              fontWeight={400}
              fontSize="18px"
              textAlign="center"
              lineHeight="24px"
              color="white"
              maxWidth="160px"
              top="50px"
              left="100px"
              position="absolute"
              zIndex="1"
            >
              {t('publicLandingPage.securityConcerns.govHighlySecured')}
            </Text>
            <Text
              as="sub"
              fontWeight={400}
              fontSize="11px"
              textAlign="center"
              lineHeight="16px"
              color="white"
              bottom="70px"
              left="145px"
              position="absolute"
              zIndex="1"
            >
              {t('publicLandingPage.securityConcerns.govHybridCloud')}
            </Text>
            <Image src={cloudIcons} bottom="40px" left="130px" position="absolute" zIndex="1" />
          </Box>
          <Stack gap="12px" maxWidth="612px">
            <Text as="h4" margin="0" fontWeight={600} fontSize="20px" lineHeight="28px">
              <Trans i18nKey="publicLandingPage.securityConcerns.govSeamlesslyIntegrate" />
            </Text>
            <Text as="p" margin="0" fontWeight={400} fontSize="16px" lineHeight="22px">
              <Trans i18nKey="publicLandingPage.securityConcerns.govSecureYourGovCloud" />
            </Text>
          </Stack>
        </Flex>
      )}
      {!isGovCloudRegion ? (
        <Text
          as="h2"
          margin="0"
          marginBottom="80px"
          textAlign="center"
          maxWidth="1000px"
          fontWeight={700}
          fontSize="36px"
          lineHeight="54px"
        >
          <Trans i18nKey="publicLandingPage.securityConcerns.secureYourBusiness" />
        </Text>
      ) : (
        <Text
          margin="0"
          marginBottom="80px"
          textAlign="center"
          maxWidth="1000px"
          fontWeight={400}
          fontFamily="interstate"
          fontSize="48px"
          lineHeight="58px"
        >
          <Trans i18nKey="publicLandingPage.securityConcerns.solveYourConcerns" />
        </Text>
      )}
      <Box position="relative">
        <Image
          src={outlineBackground}
          position="absolute"
          top={isGovCloudRegion ? '-754px' : '-80px'}
          left={isGovCloudRegion ? '-754px' : '-707px'}
        />
      </Box>
      {!isGovCloudRegion && (
        <Image
          src={cloudGraphic}
          maxHeight="725px"
          marginBottom="80px"
          maxWidth="100%"
          zIndex="1"
        />
      )}
      <CollapsibleCards
        cards={[
          {
            heading: t(`publicLandingPage.securityConcerns.${tileContentKey}.firstTile.heading`),
            icon: <Image src={CloudComplianceIcon} height="40px" />,
            body: (
              <DetailsBody
                content={t(
                  `publicLandingPage.securityConcerns.${tileContentKey}.firstTile.content`
                )}
                learnMoreLink={t(
                  `publicLandingPage.securityConcerns.${tileContentKey}.firstTile.learnMoreLink`
                )}
              />
            ),
          },
          {
            heading: t(`publicLandingPage.securityConcerns.${tileContentKey}.secondTile.heading`),
            icon: <Image src={CloudNativeIcon} height="40px" />,
            body: (
              <DetailsBody
                content={t(
                  `publicLandingPage.securityConcerns.${tileContentKey}.secondTile.content`
                )}
                learnMoreLink={t(
                  `publicLandingPage.securityConcerns.${tileContentKey}.secondTile.learnMoreLink`
                )}
              />
            ),
          },
          {
            heading: t(`publicLandingPage.securityConcerns.${tileContentKey}.thirdTile.heading`),
            icon: <Image src={CloudDataCenterIcon} height="40px" />,
            body: (
              <DetailsBody
                content={t(
                  `publicLandingPage.securityConcerns.${tileContentKey}.thirdTile.content`
                )}
                learnMoreLink={t(
                  `publicLandingPage.securityConcerns.${tileContentKey}.thirdTile.learnMoreLink`
                )}
              />
            ),
          },
        ]}
      />
      {isGovCloudRegion && (
        <>
          <Stack alignItems="center" marginTop="80px">
            <Image src={cloudOneGovernmentDiagram} maxHeight="947px" maxWidth="100%" />
          </Stack>
          <Flex justifyContent="center" marginTop="80px" marginBottom="40px">
            <Stack
              position="relative"
              width="100%"
              maxWidth="1200px"
              boxShadow="0px 0px 20px rgba(0, 0, 0, 0.25)"
              paddingY="48px"
              borderRadius="16px"
            >
              <Text
                fontSize="42px"
                paddingX={{ sm: '10%', md: '20px' }}
                lineHeight="52px"
                marginBottom="10x"
                textAlign="center"
              >
                {t('publicLandingPage.fedRamp.heading.fedRamp')}
                <Text as="sup" display="inline">
                  &#174;
                </Text>
                {t('publicLandingPage.fedRamp.heading.inProcess')}
                <Text as="sup" display="inline">
                  &trade;
                </Text>
                {t('publicLandingPage.fedRamp.heading.forGovernment')}
              </Text>
              <Text
                fontSize="24px"
                fontWeight="normal"
                lineHeight="32px"
                textAlign="center"
                marginBottom="40px"
                width="100%"
                paddingX="10%"
              >
                {t('publicLandingPage.fedRamp.content')}
              </Text>
              <Stack paddingLeft="56px" alignItems="left" width="100%">
                <Image src={fedRampLogo} width={271} height={68} />
              </Stack>
            </Stack>
          </Flex>
        </>
      )}
    </Stack>
  );
};

SecurityConcernsSection.displayName = 'SecurityConcernsSection';

export default SecurityConcernsSection;
